/**
 * DateSelectionModeField Component
 * Field for selecting date selection mode
 */

import React, { useState, useEffect } from 'react';
import {
    FormControl,
    FormLabel,
    FormHelperText,
    Select,
    MenuItem,
    FormControl as MuiFormControl,
} from '@mui/material';
import { ButtonGroup, Button } from '@dynamon-as/ui';
import { DATE_SELECTION_MODES, RequestType } from '@features/requests/types';
import type { DateSelectionMode } from '@features/requests/types/dateSelectionTypes';
import { useI18n } from '@shared/i18n';
import styles from './DateSelectionModeField.module.css';

/**
 * Configuration for which modes to show based on request type
 */
const REQUEST_TYPE_MODES: Record<RequestType, DateSelectionMode[]> = {
    [RequestType.VACATION]: ['period', 'single', 'loose_days'],
    [RequestType.PAID_LEAVE]: ['period', 'single', 'loose_days'],
    [RequestType.SHIFT_PREFERENCE]: ['period', 'single'],
};

/**
 * Props for DateSelectionModeField component
 */
export interface DateSelectionModeFieldProps {
    /** Currently selected mode */
    value: DateSelectionMode;
    /** Callback when mode changes */
    onChange: (value: DateSelectionMode) => void;
    /** Whether the field is disabled */
    disabled?: boolean;
    /** Modes that should be disabled individually */
    disabledModes?: DateSelectionMode[];
    /** Request type to determine which modes to show */
    requestType?: RequestType;
}

/**
 * Return type for useDateSelectionModes hook
 */
interface UseDateSelectionModesReturn {
    modesToShow: DateSelectionMode[];
    isShiftPreference: boolean;
    disabledModes: DateSelectionMode[];
}

/**
 * Custom hook to get the modes to display based on request type
 */
function useDateSelectionModes(
    requestType: RequestType | undefined,
    disabledModes: DateSelectionMode[] | undefined,
): UseDateSelectionModesReturn {
    const isShiftPreference = requestType === RequestType.SHIFT_PREFERENCE;

    // Get modes to show based on request type
    const modesToShow = requestType
        ? REQUEST_TYPE_MODES[requestType]
        : DATE_SELECTION_MODES.map((m): DateSelectionMode => m.id).filter(
              (id: DateSelectionMode): boolean => id !== 'shift',
          );

    return {
        modesToShow,
        isShiftPreference,
        disabledModes: disabledModes ?? [],
    };
}

/**
 * DateSelectionModeField component
 */
export function DateSelectionModeField({
    value,
    onChange,
    disabled = false,
    disabledModes,
    requestType,
}: DateSelectionModeFieldProps): React.ReactElement {
    const { t } = useI18n();
    const {
        modesToShow,
        isShiftPreference,
        disabledModes: globalDisabledModes,
    } = useDateSelectionModes(requestType, disabledModes);

    // Detect mobile screen size
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = (): void => {
            setIsMobile(window.innerWidth <= 768);
        };

        // Initial check
        checkMobile();

        // Add event listener for window resize
        window.addEventListener('resize', checkMobile);

        // Cleanup
        return (): void => window.removeEventListener('resize', checkMobile);
    }, []);

    const currentMode = DATE_SELECTION_MODES.find(
        (m): boolean => m.id === value,
    );

    return (
        <div
            className={styles.field}
            data-testid="date-selection-mode-field"
        >
            <FormControl fullWidth>
                <FormLabel
                    id="date-selection-mode-label"
                    className={styles.fieldLabel}
                    htmlFor="date-selection-mode-buttons"
                >
                    {isShiftPreference
                        ? t('requests.form.dateSelection.shiftType')
                        : t('requests.form.dateSelection.label')}
                </FormLabel>
                <div className={styles.selectionModePaper}>
                    {isMobile ? (
                        <MuiFormControl
                            fullWidth
                            size="small"
                        >
                            <Select
                                value={value}
                                onChange={(e): void =>
                                    onChange(
                                        e.target.value as DateSelectionMode,
                                    )
                                }
                                disabled={disabled}
                                displayEmpty
                                id="date-selection-mode-buttons"
                                data-testid="mode-select"
                            >
                                <MenuItem
                                    value=""
                                    disabled
                                >
                                    {isShiftPreference
                                        ? t(
                                              'requests.form.dateSelection.shiftType',
                                          )
                                        : t(
                                              'requests.form.dateSelection.label',
                                          )}
                                </MenuItem>
                                {modesToShow.map(
                                    (
                                        modeId: DateSelectionMode,
                                    ): React.ReactElement | null => {
                                        const mode = DATE_SELECTION_MODES.find(
                                            (m): boolean => m.id === modeId,
                                        );
                                        if (!mode) return null;

                                        const isModeDisabled =
                                            disabled ??
                                            globalDisabledModes.includes(
                                                mode.id,
                                            );

                                        return (
                                            <MenuItem
                                                key={mode.id}
                                                value={mode.id}
                                                disabled={isModeDisabled}
                                                data-testid={`mode-${mode.id}-option`}
                                            >
                                                {mode.label}
                                            </MenuItem>
                                        );
                                    },
                                )}
                            </Select>
                        </MuiFormControl>
                    ) : (
                        <ButtonGroup
                            size="small"
                            variant="outlined"
                            id="date-selection-mode-buttons"
                            data-testid="mode-buttons"
                        >
                            {modesToShow.map(
                                (
                                    modeId: DateSelectionMode,
                                ): React.ReactElement | null => {
                                    const mode = DATE_SELECTION_MODES.find(
                                        (m): boolean => m.id === modeId,
                                    );
                                    if (!mode) return null;

                                    const isModeDisabled =
                                        disabled ??
                                        globalDisabledModes.includes(mode.id);

                                    return (
                                        <Button
                                            key={mode.id}
                                            text={mode.label}
                                            variant={
                                                value === mode.id
                                                    ? 'normal'
                                                    : 'outlined'
                                            }
                                            onClick={(): void => {
                                                if (!isModeDisabled) {
                                                    onChange(mode.id);
                                                }
                                            }}
                                            disabled={isModeDisabled}
                                            className={
                                                styles.selectionModeButton
                                            }
                                            type="button"
                                            aria-label={`Velg ${mode.label.toLowerCase()} modus`}
                                            aria-disabled={isModeDisabled}
                                            data-testid={`mode-${mode.id}-button`}
                                        />
                                    );
                                },
                            )}
                        </ButtonGroup>
                    )}
                </div>
                {currentMode && (
                    <FormHelperText data-testid="mode-description">
                        {currentMode.description}
                    </FormHelperText>
                )}
            </FormControl>
        </div>
    );
}

export default DateSelectionModeField;
